/**
 * Which draft painted which properties onto an element, per element.
 *
 * A draft is painted on every instance of its source element, and sibling instances
 * are different designer nodes — so one element can carry paint from more than one
 * live draft at a time. Keeping an entry per draft identity is what lets a strip touch
 * only what the draft it runs for painted, and leave a property another draft is still
 * holding alone.
 *
 * Module-level rather than a component ref because two writers paint these elements
 * and both have to answer that question the same way: `VCCStyleCascadeEffect` repaints
 * the cascade after every dispatch, and the Style Panel paints each keystroke before
 * the dispatch that triggers the effect. With separate records the panel's restore had
 * no way to see a sibling node's live draft, so clearing a field on one instance took
 * that draft's paint off the canvas while it was still pending. Keyed weakly, so a
 * rebuilt or unmounted element takes its entry with it — same lifetime as
 * [[vccInstanceStyleBaselines]], which the restore paths read alongside this.
 */
const instancePaintedProps = new WeakMap<Element, Map<string, Set<string>>>();

/**
 * `instance`'s painted-property record, created and stored on first use. Mutable: the
 * caller owns the entries for the draft it paints for.
 */
export function ensureVccInstancePaintedProps(
  instance: Element
): Map<string, Set<string>> {
  const existing = instancePaintedProps.get(instance);
  if (existing) return existing;

  const created = new Map<string, Set<string>>();
  instancePaintedProps.set(instance, created);
  return created;
}

export function findVccInstancePaintedProps(
  instance: Element
): ReadonlyMap<string, Set<string>> | undefined {
  return instancePaintedProps.get(instance);
}

/**
 * Every draft identity — other than `excludeDraftIdentity` — recorded as painting
 * `prop` on `instance`, in insertion order. Empty when none is.
 *
 * The veto on every restore path: such a draft is still live and still counted for the
 * write, so putting the pre-paint value back here would take it off the canvas while
 * the user is still editing it. Identities rather than a boolean because the property's
 * correct value is the holding draft's cascade, not the baseline and not whatever the
 * one standing down last painted.
 *
 * All of them, not the first, because a record is not proof a draft is live and every
 * caller narrows further: the discard path vetoes only for an identity whose draft
 * outlives the revert, and the panel only for an identity that still resolves a value
 * for the property. Answering with one identity would let a stale or already-discarded
 * entry stand in for a live one and fail that test on its behalf — stripping a surviving
 * draft's paint, or falling back to the baseline while a draft still holds the property.
 */
export function findVccInstancePropPainters(
  instance: Element,
  prop: string,
  excludeDraftIdentity: string
): string[] {
  const paintedByDraft = instancePaintedProps.get(instance);
  if (!paintedByDraft) return [];

  const painters: string[] = [];
  for (const [paintingIdentity, props] of paintedByDraft) {
    if (paintingIdentity !== excludeDraftIdentity && props.has(prop)) {
      painters.push(paintingIdentity);
    }
  }
  return painters;
}
